import { useEffect } from "react";

import { useAppDispatch } from "app/hooks";

import { toggleQuickLauncher } from "./quick-launcher.slice";

/** True on macOS-ish platforms, where the palette convention is ⌘K rather than Ctrl+K. */
export const isAppleDevice = (): boolean =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);

/** "⌘K" or "Ctrl K" — for the trigger's tooltip and the launcher's footer. */
export const getQuickLauncherHotkeyLabel = (): string =>
  isAppleDevice() ? "⌘K" : "Ctrl K";

/**
 * Binds ⌘K / Ctrl+K to the quick launcher while `enabled`.
 *
 * Mounted once, in `Layout`, so the binding exists on every authenticated page.
 * Listens on `window` in the **capture** phase: `practice-selector.component.tsx`
 * calls `stopPropagation()` on keydowns inside its search box, but that only stops
 * the React (bubble-phase) tree, so a capture-phase window listener still sees the
 * event.
 *
 * Deliberately inert while a rich-text editor has focus — TinyMCE binds Ctrl/⌘+K
 * to "insert link", and the editor should keep owning it. TinyMCE's default iframe
 * mode never reaches this listener at all; the `isEditableTarget` guard covers
 * inline/contenteditable surfaces, which do.
 */
export default function useQuickLauncherHotkey(enabled: boolean) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!enabled) return undefined;

    const isEditableTarget = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      return (
        target.isContentEditable || Boolean(target.closest(".tox-tinymce"))
      );
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // `event.key` is "k"/"K" depending on Shift/CapsLock; compare case-insensitively.
      if (event.key?.toLowerCase() !== "k") return;
      // Exactly one of ⌘ / Ctrl, and neither Alt nor Shift — so ⌘⇧K and Ctrl+Alt+K
      // stay available to the browser and to any future binding.
      const hasPrimaryModifier = isAppleDevice()
        ? event.metaKey && !event.ctrlKey
        : event.ctrlKey && !event.metaKey;
      if (!hasPrimaryModifier || event.altKey || event.shiftKey) return;
      if (isEditableTarget(event.target)) return;

      // Chrome maps ⌘K / Ctrl+K to "focus the address bar"; claim it before then.
      event.preventDefault();
      dispatch(toggleQuickLauncher("hotkey"));
    };

    window.addEventListener("keydown", handleKeyDown, { capture: true });
    return () =>
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [dispatch, enabled]);
}
